/**
 * `CvRoute` — guardia di rotta difensiva per la sezione "CV".
 *
 * A differenza di `RoleRoute` (basata sul `Role`), l'accesso alla sezione CV è
 * governato da un flag per‑utente `cvEnabled` (gestito dall'Admin dalla lista
 * utenti). L'`admin` ha comunque sempre accesso.
 *
 * Come le altre guardie, questa è DIFENSIVA lato client: migliora la UX
 * evitando di mostrare pagine non autorizzate, ma NON è la fonte autorevole
 * dell'autorizzazione, che resta l'API (403 tramite `requireCvAccess`).
 *
 * Comportamento:
 *  - richiede una sessione autenticata (come `ProtectedRoute`);
 *  - mentre il profilo autorevole (`/api/me`) è in caricamento, mostra un
 *    fallback per non negare l'accesso prima di conoscere `cvEnabled`;
 *  - se l'utente non è `admin` e non ha `cvEnabled`, mostra la pagina di
 *    "accesso negato" (o reindirizza, se `deniedRedirectTo` è fornito).
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { AccessDenied } from './RoleRoute';

interface CvRouteProps {
  /** Contenuto protetto. Se assente, usa `<Outlet />` (rotte annidate). */
  children?: ReactNode;
  /** Percorso di redirect per utenti non autenticati. */
  redirectTo?: string;
  /**
   * Percorso di redirect per utenti autenticati ma senza accesso al CV.
   * Se omesso, viene mostrata la pagina di "accesso negato" in-place.
   */
  deniedRedirectTo?: string;
  /** Fallback mostrato durante il caricamento della sessione/profilo. */
  fallback?: ReactNode;
}

export function CvRoute({
  children,
  redirectTo = '/login',
  deniedRedirectTo,
  fallback = <p role="status">Caricamento della sessione…</p>,
}: CvRouteProps) {
  const { isLoading, isProfileLoading, isAuthenticated, role, me } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // Il flag `cvEnabled` vive solo nel profilo autorevole (`/api/me`).
  // Finché non è caricato, attendiamo per non negare l'accesso a un utente
  // abilitato il cui profilo non è ancora arrivato.
  if (me === null && isProfileLoading) {
    return <>{fallback}</>;
  }

  const isAllowed = role === 'admin' || me?.cvEnabled === true;
  if (!isAllowed) {
    if (deniedRedirectTo) {
      return <Navigate to={deniedRedirectTo} replace />;
    }
    return <AccessDenied />;
  }

  return <>{children ?? <Outlet />}</>;
}
